import { create } from 'zustand';
import {
  lookAtThatApi,
  type LookAtThatAdapter,
  type LookAtThatCalibration,
  type LookAtThatChatMessage,
  type LookAtThatExploreItem,
  type LookAtThatMemoryNote,
  type LookAtThatStatus,
} from '../lib/lookAtThatApi';

export interface LookAtThatLog {
  id: string;
  ts: string;
  message: string;
  event: string;
}

/** Virtual bot cursor drawn over the viewer (screenshot-space coordinates). */
export interface LookAtThatCursor {
  x: number;
  y: number;
  /** click | move | scroll | drag — drives the overlay animation. */
  kind: string;
  /** Monotonic sequence so equal coordinates still retrigger the ripple. */
  seq: number;
}

interface LookAtThatState {
  runId: string | null;
  status: LookAtThatStatus | null;
  sessionId: string | null;
  screenshot: string | null;
  width: number | null;
  height: number | null;
  running: boolean;
  executing: boolean;
  awaitingCredentials: boolean;
  recipeUser: string | null;
  lastAction: string | null;
  error: string | null;
  logs: LookAtThatLog[];
  explore: LookAtThatExploreItem[];
  loading: boolean;
  calibration: LookAtThatCalibration | null;
  calibrating: boolean;
  memory: LookAtThatMemoryNote[];
  /** Test currently being played live (null when queue idle). */
  playingKey: string | null;
  /** Tests still waiting in the play queue. */
  queuedKeys: string[];
  /** Live-play verdicts observed this session, for row chips. */
  playVerdicts: Record<string, string>;
  cursor: LookAtThatCursor | null;
  /** True while the bot is typing (cursor overlay shows a keyboard hint). */
  typing: boolean;
  chat: LookAtThatChatMessage[];
  /** True between sending a chat question and receiving the answer. */
  chatPending: boolean;
  adapter: string;
  adapters: LookAtThatAdapter[];
  setAdapter: (id: string) => void;

  load: (runId: string) => Promise<void>;
  start: (gameUrl: string, task?: string, adapter?: string) => Promise<void>;
  execute: (task: string) => Promise<void>;
  explorePrompt: (prompt: string) => Promise<void>;
  calibrate: () => Promise<void>;
  sendChat: (message: string) => Promise<void>;
  playTests: (testKeys: string[]) => Promise<void>;
  stopQueue: () => Promise<void>;
  deleteMemoryNote: (noteId: string) => Promise<void>;
  refreshMemory: () => Promise<void>;
  submitCredentials: (user: string, password: string, extra?: string) => Promise<void>;
  stop: () => Promise<void>;
  refresh: () => Promise<void>;
  applyProgress: (msg: {
    runId: string;
    event: string;
    sessionId?: string;
    message?: string;
    screenshot?: string;
    width?: number | null;
    height?: number | null;
    awaitingCredentials?: boolean;
    recipeUser?: string | null;
    exploreItem?: LookAtThatExploreItem;
    actionType?: string;
    testKey?: string;
    playVerdict?: string;
    queuedKeys?: string[];
    memoryNotes?: string[];
    adapter?: string;
    x?: number;
    y?: number;
  }) => void;
  clear: () => void;
}

let logSeq = 0;
/** Ignore leftover WS events after the tester clicks Stop. */
let lastStoppedSessionId: string | null = null;

const busyEvents = new Set([
  'analyzing',
  'clicking',
  'typing',
  'scrolling',
  'navigating',
  'turn_start',
  'screenshot_taken',
  'waiting_for_ai_response',
]);

export const useLookAtThatStore = create<LookAtThatState>((set, get) => ({
  runId: null,
  status: null,
  sessionId: null,
  screenshot: null,
  width: null,
  height: null,
  running: false,
  executing: false,
  awaitingCredentials: false,
  recipeUser: null,
  lastAction: null,
  error: null,
  logs: [],
  explore: [],
  loading: false,
  calibration: null,
  calibrating: false,
  memory: [],
  playingKey: null,
  queuedKeys: [],
  playVerdicts: {},
  cursor: null,
  typing: false,
  chat: [],
  chatPending: false,
  adapter: 'openai_ga',
  adapters: [],
  setAdapter(id) {
    if (!id) return;
    set({ adapter: id });
  },

  async load(runId) {
    const different = get().runId !== null && get().runId !== runId;
    set({
      runId,
      loading: true,
      error: null,
      ...(different
        ? {
            sessionId: null,
            screenshot: null,
            running: false,
            executing: false,
            awaitingCredentials: false,
            logs: [],
            explore: [],
            calibration: null,
            calibrating: false,
            memory: [],
            playingKey: null,
            queuedKeys: [],
            playVerdicts: {},
            chat: [],
            chatPending: false,
          }
        : {}),
    });
    try {
      const status = await lookAtThatApi.status(runId);
      set({
        status,
        sessionId: status.sessionId,
        running: Boolean(status.sessionId),
        recipeUser: status.recipeUser,
        explore: status.explore ?? [],
        calibration: status.calibration ?? null,
        memory: status.memory ?? [],
        playingKey: status.playQueue?.current ?? null,
        queuedKeys: status.playQueue?.pending ?? [],
        chat: status.chat ?? [],
        adapters: status.adapters ?? [],
      });
    } catch (err) {
      set({ error: (err as Error).message });
    } finally {
      set({ loading: false });
    }
  },

  async start(gameUrl, task, adapter) {
    const runId = get().runId;
    if (!runId) return;
    lastStoppedSessionId = null;
    const chosen = adapter || get().adapter || 'openai_ga';
    set({ error: null, executing: true, running: true, logs: [], awaitingCredentials: false, adapter: chosen });
    try {
      const data = await lookAtThatApi.start(runId, { gameUrl, task, adapter: chosen });
      set({ sessionId: data.sessionId, running: true, adapter: data.adapter || chosen });
    } catch (err) {
      set({ error: (err as Error).message, running: false, executing: false });
    }
  },

  async execute(task) {
    const { runId, sessionId } = get();
    if (!runId || !sessionId) return;
    set({ executing: true, error: null });
    try {
      await lookAtThatApi.execute(runId, sessionId, task);
    } catch (err) {
      set({ error: (err as Error).message, executing: false });
    }
  },

  async explorePrompt(prompt) {
    const { runId, sessionId } = get();
    if (!runId || !sessionId) return;
    set({ executing: true, error: null });
    try {
      await lookAtThatApi.explore(runId, sessionId, prompt);
    } catch (err) {
      set({ error: (err as Error).message, executing: false });
    }
  },

  async calibrate() {
    const { runId, sessionId } = get();
    if (!runId || !sessionId) return;
    set({ executing: true, calibrating: true, error: null });
    try {
      await lookAtThatApi.calibrate(runId, sessionId);
    } catch (err) {
      set({ error: (err as Error).message, executing: false, calibrating: false });
    }
  },

  async sendChat(message) {
    const { runId, sessionId } = get();
    const text = message.trim();
    if (!runId || !sessionId || !text) return;
    // Optimistic local echo; the server persists the canonical copy.
    const local: LookAtThatChatMessage = {
      id: `local-${Date.now()}`,
      role: 'user',
      content: text,
      ts: new Date().toISOString(),
    };
    set((s) => ({ chat: [...s.chat, local], chatPending: true, executing: true, error: null }));
    try {
      await lookAtThatApi.chat(runId, sessionId, text);
    } catch (err) {
      set((s) => ({
        error: (err as Error).message,
        chatPending: false,
        executing: false,
        chat: [
          ...s.chat,
          {
            id: `local-err-${Date.now()}`,
            role: 'assistant',
            content: `Could not send the question: ${(err as Error).message}`,
            ts: new Date().toISOString(),
          },
        ],
      }));
    }
  },

  async playTests(testKeys) {
    const { runId, sessionId } = get();
    if (!runId || !sessionId || !testKeys.length) return;
    set({ error: null, queuedKeys: testKeys });
    try {
      await lookAtThatApi.play(runId, sessionId, testKeys);
    } catch (err) {
      set({ error: (err as Error).message, queuedKeys: [], playingKey: null });
    }
  },

  async stopQueue() {
    const { runId, sessionId } = get();
    if (!runId || !sessionId) return;
    try {
      await lookAtThatApi.playStop(runId, sessionId);
      set({ queuedKeys: [] });
    } catch (err) {
      set({ error: (err as Error).message });
    }
  },

  async deleteMemoryNote(noteId) {
    const runId = get().runId;
    if (!runId) return;
    try {
      const notes = await lookAtThatApi.deleteMemoryNote(runId, noteId);
      set({ memory: notes });
    } catch (err) {
      set({ error: (err as Error).message });
    }
  },

  async refreshMemory() {
    const runId = get().runId;
    if (!runId) return;
    try {
      set({ memory: await lookAtThatApi.memory(runId) });
    } catch {
      /* non-fatal */
    }
  },

  async submitCredentials(user, password, extra) {
    const { runId, sessionId } = get();
    if (!runId || !sessionId) return;
    set({ error: null });
    try {
      await lookAtThatApi.credentials(runId, sessionId, { user, password, extra });
      set({ awaitingCredentials: false });
    } catch (err) {
      set({ error: (err as Error).message });
    }
  },

  async stop() {
    const { runId, sessionId } = get();
    if (!runId || !sessionId) return;
    lastStoppedSessionId = sessionId;
    set({
      running: false,
      executing: false,
      calibrating: false,
      awaitingCredentials: false,
      chatPending: false,
      playingKey: null,
      queuedKeys: [],
      sessionId: null,
      cursor: null,
      typing: false,
    });
    try {
      await lookAtThatApi.stop(runId, sessionId);
    } catch (err) {
      set({ error: (err as Error).message });
    }
  },

  async refresh() {
    const { runId, sessionId } = get();
    if (!runId || !sessionId) return;
    try {
      await lookAtThatApi.refresh(runId, sessionId);
    } catch (err) {
      set({ error: (err as Error).message });
    }
  },

  applyProgress(msg) {
    if (get().runId && msg.runId !== get().runId) return;
    if (lastStoppedSessionId && msg.sessionId === lastStoppedSessionId) return;
    const entry: LookAtThatLog = {
      id: `${Date.now()}-${++logSeq}`,
      ts: new Date().toISOString(),
      message: msg.message || msg.event,
      event: msg.event,
    };
    set((s) => {
      const silent = msg.event === 'screenshot' || msg.event === 'frame';
      const next: Partial<LookAtThatState> = {
        logs: silent ? s.logs : [...s.logs, entry].slice(-200),
      };
      if (msg.sessionId) next.sessionId = msg.sessionId;
      if (typeof msg.adapter === 'string' && msg.adapter) next.adapter = msg.adapter;
      if (msg.screenshot) {
        next.screenshot = msg.screenshot;
        next.width = msg.width ?? s.width;
        next.height = msg.height ?? s.height;
      }
      // Bot cursor overlay: the engine reports target coordinates right
      // before it acts, so the viewer can show where the bot is reaching.
      const cursorKinds: Record<string, string> = {
        clicking: 'click',
        moving: 'move',
        scrolling: 'scroll',
        dragging: 'drag',
      };
      if (typeof msg.x === 'number' && typeof msg.y === 'number' && cursorKinds[msg.event]) {
        next.cursor = {
          x: msg.x,
          y: msg.y,
          kind: cursorKinds[msg.event],
          seq: (s.cursor?.seq ?? 0) + 1,
        };
      }
      if (msg.event === 'typing') next.typing = true;
      else if (!silent && msg.event !== 'key_press') next.typing = false;
      if (msg.event === 'awaiting_credentials') {
        next.awaitingCredentials = true;
        next.executing = false;
        if (msg.recipeUser) next.recipeUser = msg.recipeUser;
      }
      if (msg.event === 'ready' || msg.event === 'completed') {
        next.executing = false;
        next.running = msg.event !== 'completed';
        next.cursor = null;
        next.typing = false;
      }
      if (msg.event === 'error') {
        next.error = msg.message ?? 'Look at that error';
        next.executing = false;
      }
      if (busyEvents.has(msg.event)) next.executing = true;
      if (msg.actionType) next.lastAction = msg.actionType;
      if (msg.exploreItem) {
        next.explore = [msg.exploreItem, ...s.explore.filter((x) => x.id !== msg.exploreItem!.id)];
      }
      if (msg.event === 'chat_answer') {
        next.chatPending = false;
        if (msg.message) {
          next.chat = [
            ...s.chat,
            {
              id: `ws-${Date.now()}-${logSeq}`,
              role: 'assistant',
              content: msg.message,
              ts: new Date().toISOString(),
            },
          ];
        }
      }
      if (msg.event === 'calibrate_start') {
        next.calibrating = true;
        next.executing = true;
      }
      if (msg.event === 'calibration_saved') {
        next.calibrating = false;
      }
      if (msg.event === 'play_test' && msg.testKey) {
        next.playingKey = msg.testKey;
        if (msg.queuedKeys) next.queuedKeys = msg.queuedKeys;
      }
      if (msg.event === 'play_done' || msg.event === 'play_error' || msg.event === 'play_skipped') {
        next.playingKey = null;
        if (msg.queuedKeys) next.queuedKeys = msg.queuedKeys;
        if (msg.event === 'play_done' && msg.testKey && msg.playVerdict) {
          next.playVerdicts = { ...s.playVerdicts, [msg.testKey]: msg.playVerdict };
        }
      }
      if (msg.event === 'play_queue_started' && msg.queuedKeys) {
        next.queuedKeys = msg.queuedKeys;
      }
      if (msg.event === 'play_queue_done' || msg.event === 'play_queue_stopped') {
        next.playingKey = null;
        next.queuedKeys = [];
      }
      if (msg.event === 'stopped') {
        next.running = false;
        next.executing = false;
        next.awaitingCredentials = false;
        next.playingKey = null;
        next.queuedKeys = [];
        next.cursor = null;
        next.typing = false;
        next.chatPending = false;
      }
      return next;
    });
    // Server-side files changed: refetch the light metadata (atlas / memory).
    if (msg.event === 'calibration_saved' || msg.event === 'memory_saved') {
      const runId = get().runId;
      if (runId) {
        if (msg.event === 'memory_saved') void get().refreshMemory();
        else
          void lookAtThatApi.status(runId).then((status) =>
            set({ calibration: status.calibration ?? null, memory: status.memory ?? [] }),
          );
      }
    }
  },

  clear() {
    set({
      runId: null,
      status: null,
      sessionId: null,
      screenshot: null,
      running: false,
      executing: false,
      awaitingCredentials: false,
      logs: [],
      explore: [],
      error: null,
      calibration: null,
      calibrating: false,
      memory: [],
      playingKey: null,
      queuedKeys: [],
      playVerdicts: {},
      cursor: null,
      typing: false,
      chat: [],
      chatPending: false,
      adapter: 'openai_ga',
    });
  },
}));
